/**
 * ImpersonationBanner — warning banner shown when the current session is
 * being impersonated by a platform admin.
 *
 * - Tenant perspective (impersonated user): prominent red banner with admin
 *   identity, reason, time remaining, edit-mode indicator, and "Revoke".
 * - Admin perspective (admin viewing their active session from another tab):
 *   softer amber hint with "Enable editing" + "End session".
 *
 * The "Enable editing" step is a separate audit row — sessions always start
 * read-only, and elevating them to write requires a fresh reason.
 */

import { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, Stack, TextField, Typography,
} from '@mui/material';
import { IMPERSONATION_COLORS } from '@/theme/colorTokens';
import { useAuthStore } from '@/stores/authStore';
import apiClient from '@/api/client';
import { platformApi } from '@/api/endpoints/platform';

function minutesUntil(isoExpiresAt: string): number {
  const diffMs = new Date(isoExpiresAt).getTime() - Date.now();
  return Math.max(0, Math.floor(diffMs / 60_000));
}

export default function ImpersonationBanner() {
  const impersonation = useAuthStore((s) => s.impersonation);
  const setImpersonation = useAuthStore((s) => s.setImpersonation);
  const [tick, setTick] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Elevation dialog state
  const [elevateOpen, setElevateOpen] = useState(false);
  const [elevateReason, setElevateReason] = useState('');
  const [elevateTicket, setElevateTicket] = useState('');

  // Re-render every 30s so the time-remaining badge stays fresh.
  useEffect(() => {
    if (!impersonation) return;
    const id = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(id);
  }, [impersonation]);

  // Reference `tick` so the setInterval re-render actually has an effect;
  // the value is computed for its side-effect only.
  void tick;

  if (!impersonation) return null;

  const minsLeft = minutesUntil(impersonation.expires_at);
  const isAdmin = impersonation.is_admin_perspective;
  const writeMode = impersonation.write_mode;

  const onRevokeByTenant = async () => {
    setBusy(true);
    try {
      await apiClient.post('/auth/impersonation/revoke');
      // Server cleared the cookie. Hard reload forces re-auth.
      window.location.href = '/login';
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'Failed to revoke session');
      setBusy(false);
    }
  };

  const onEndByAdmin = async () => {
    setBusy(true);
    try {
      await apiClient.post('/platform/impersonate/end');
      window.location.href = '/platform/overview';
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'Failed to end session');
      setBusy(false);
    }
  };

  const onSubmitElevate = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await platformApi.elevateImpersonation(
        elevateReason,
        elevateTicket || undefined,
      );
      setImpersonation({
        ...impersonation,
        write_mode: res.data.write_mode,
        elevated_at: res.data.elevated_at,
      });
      setElevateOpen(false);
      setElevateReason('');
      setElevateTicket('');
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'Failed to enable editing');
    } finally {
      setBusy(false);
    }
  };

  if (isAdmin) {
    return (
      <>
        <Alert
          severity={writeMode ? 'error' : 'warning'}
          sx={{ borderRadius: 0, '& .MuiAlert-message': { width: '100%' } }}
        >
          <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Active impersonation session ({minsLeft} min remaining)
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Reason: {impersonation.reason}
              </Typography>
              {error && (
                <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                  {error}
                </Typography>
              )}
            </Box>
            {writeMode ? (
              <Chip
                size="small"
                label="Editing enabled"
                color="error"
                sx={{ fontWeight: 700 }}
              />
            ) : (
              <Button
                size="small"
                variant="contained"
                color="warning"
                onClick={() => setElevateOpen(true)}
                disabled={busy}
              >
                Enable editing
              </Button>
            )}
            <Button size="small" variant="outlined" color="inherit" onClick={onEndByAdmin} disabled={busy}>
              End session
            </Button>
          </Stack>
        </Alert>

        {/* Elevation dialog */}
        <Dialog open={elevateOpen} onClose={() => setElevateOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Enable editing for this session?</DialogTitle>
          <DialogContent>
            <Alert severity="error" sx={{ mb: 2 }}>
              Write access lets you change the tenant's data as if you were
              their admin. Every change is attributed to the tenant's admin,
              so add a separate note/ticket when you do something the tenant
              didn't request.
            </Alert>
            <Stack spacing={2}>
              <TextField
                label="Reason for enabling editing"
                size="small"
                multiline
                minRows={2}
                value={elevateReason}
                onChange={(e) => setElevateReason(e.target.value)}
                helperText="At least 10 characters. Captured as a separate audit row."
                fullWidth
                autoFocus
              />
              <TextField
                label="Support ticket / Slack link (optional)"
                size="small"
                value={elevateTicket}
                onChange={(e) => setElevateTicket(e.target.value)}
                fullWidth
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setElevateOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              disabled={busy || elevateReason.trim().length < 10}
              onClick={onSubmitElevate}
            >
              Enable editing
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  // Tenant perspective: loud banner.
  return (
    <Alert
      severity="error"
      sx={{
        borderRadius: 0,
        bgcolor: IMPERSONATION_COLORS.bg,
        color: IMPERSONATION_COLORS.fg,
        '& .MuiAlert-icon': { color: IMPERSONATION_COLORS.fg },
        '& .MuiAlert-message': { width: '100%' },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            A platform admin ({impersonation.admin_name || impersonation.admin_email}) is
            viewing your account.
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            {writeMode ? 'Editing enabled' : 'Read-only'} · {minsLeft} min remaining · Reason: {impersonation.reason}
          </Typography>
          {error && (
            <Typography variant="caption" sx={{ color: IMPERSONATION_COLORS.accent, display: 'block' }}>
              {error}
            </Typography>
          )}
        </Box>
        <Button
          size="small"
          variant="contained"
          color="inherit"
          sx={{ color: IMPERSONATION_COLORS.bg, bgcolor: IMPERSONATION_COLORS.fg, '&:hover': { bgcolor: IMPERSONATION_COLORS.buttonHover } }}
          onClick={onRevokeByTenant}
          disabled={busy}
        >
          Revoke access
        </Button>
      </Stack>
    </Alert>
  );
}
